import { END_POINTS } from "@/constants/url";
import emptySplitApi from "@/lib/rtk";
import { CommonResponseType } from "@/types/General";

export const AuthService = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<CommonResponseType & { data: any }, { body: any }>({
      query: ({ body }) => ({
        url: `${END_POINTS.login}`,
        method: "POST",
        body,
      }),
    }),
    loginWithPhone: builder.mutation<
      CommonResponseType & { data: any },
      { body: any }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.loginWithPhone}`,
        method: "POST",
        body,
      }),
    }),
    signUp: builder.mutation<CommonResponseType & { data: any }, { body: any }>(
      {
        query: ({ body }) => ({
          url: `${END_POINTS.signUp}`,
          method: "POST",
          body,
        }),
      }
    ),

    verifyOtp: builder.mutation<
      CommonResponseType & { data: any },
      { body: any }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.verifyOtp}`,
        method: "POST",
        body,
      }),
    }),
    resendOtp: builder.mutation<
      CommonResponseType & { data: any },
      { body: any }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.resendOtp}`,
        method: "POST",
        body,
      }),
    }),
    updateProfile: builder.mutation<
      CommonResponseType & { data: any },
      { body: any }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.updateProfile}`,
        method: "POST",
        body,
      }),
    }),
    getProfile: builder.query<
      CommonResponseType & { data: any },
      void
    >({
      query: () => ({
        url: `${END_POINTS.getProfile}`,
        method: "GET",
      }),
    }),
    imageUpload: builder.mutation<
      CommonResponseType & { data: any },
      { body: FormData }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.imageUpload}`,
        method: "POST",
        body,
      }),
    }),
    verifyChangePhoneNumber: builder.mutation<
      CommonResponseType & { data: any },
      { body: { otp: string | number; type: number } }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.verifyChangePhoneNumber}`,
        method: "POST",
        body,
      }),
    }),
    changePhoneNumber: builder.mutation<
      CommonResponseType & { data: any },
      {
        body: {
          phone_code: string | number;
          phone: string | number;
          phone_country: string;
        };
      }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.changePhoneNumber}`,
        method: "POST",
        body,
      }),
    }),
    changeEmail: builder.mutation<
      CommonResponseType & { data: any },
      { body: { email: string } }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.changeEmail}`,
        method: "POST",
        body,
      }),
    }),
    forgotPassword: builder.mutation<
      CommonResponseType & { data: any },
      { body: FormData }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.forgotPassword}`,
        method: "POST",
        body,
      }),
    }),
    changePassword: builder.mutation<
      CommonResponseType & { data: any },
      { body: FormData }
    >({
      query: ({ body }) => ({
        url: `${END_POINTS.changePassword}`,
        method: "POST",
        body,
      }),
    }),
    logout: builder.mutation<
      CommonResponseType & { data: any },
      { params?: { name?: string; gender?: string; dob?: string } }
    >({
      query: ({ params }) => {
        // Build query string if params are provided
        const queryParams = new URLSearchParams();
        if (params?.name) queryParams.append("name", params.name);
        if (params?.gender) queryParams.append("gender", params.gender);
        if (params?.dob) queryParams.append("dob", params.dob);

        const queryString = queryParams.toString();
        const url = queryString
          ? `${END_POINTS.logout}?${queryString}`
          : `${END_POINTS.logout}`;

        return {
          url,
          method: "GET",
        };
      },
    }),
    deleteAccount: builder.mutation<
      CommonResponseType & { data: any },
      void
    >({
      query: () => ({
        url: `${END_POINTS.deleteAccount}`,
        method: "GET",
      }),
    }),
    getBrands: builder.query<
      CommonResponseType & { data: { data: any[]; data_count?: number; total_pages?: number } },
      { page?: number; limit?: number }
    >({
      query: ({ page = 1, limit }) => {
        const queryParams = new URLSearchParams();
        queryParams.append("page", page.toString());
        // If limit is provided, use it; otherwise use a high limit to fetch all brands
        queryParams.append("limit", (limit || 1000).toString());
        return {
          url: `${END_POINTS.brands}?${queryParams.toString()}`,
          method: "GET",
        };
      },
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginWithPhoneMutation,
  useSignUpMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useUpdateProfileMutation,
  useGetProfileQuery,
  useImageUploadMutation,
  useVerifyChangePhoneNumberMutation,
  useChangePhoneNumberMutation,
  useChangeEmailMutation,
  useForgotPasswordMutation,
  useChangePasswordMutation,
  useLogoutMutation,
  useLazyGetProfileQuery,
  useDeleteAccountMutation,
  useGetBrandsQuery,
  useLazyGetBrandsQuery,
} = AuthService;
